import { LocaleCode } from '@tink/config/src/markets';

export const normalizeLocale = (locale: LocaleCode) => locale.replace('_', '-');

const formatCurrency = (amount: number, currency: string | null, locale: LocaleCode): string => {
  const options = currency ? { style: 'currency', currency } : {};

  return new Intl.NumberFormat(normalizeLocale(locale), options).format(amount);
};

export const formatCurrencyByLocale = (
  amount: number,
  currency: string | null,
  localeCode: LocaleCode
): string => formatCurrency(amount, currency, localeCode);

/**
 * Type safe `Object.keys`.
 *
 * See {@link https://github.com/microsoft/TypeScript/pull/12253#issuecomment-263132208}
 * for more details on why this isn't built into the language.
 *
 * We're making this a separate utility, rather than overwriting the `Object` interface, to avoid
 * the edge cases mentioned in the issue above.
 */
export const typedKeys = <T extends object>(obj: T) => Object.keys(obj) as Array<keyof T>;

export const obfuscateAccountNumber = (
  accountNumber: string
): { hiddenPart: string; visiblePart: string } => {
  const VISIBLE_CHARS = accountNumber.length;

  if (VISIBLE_CHARS <= 4) {
    return {
      hiddenPart: new Array(accountNumber.length - 2 + 1).join('*'),
      visiblePart: accountNumber.slice(-2),
    };
  }

  return {
    hiddenPart: new Array(accountNumber.length - 4 + 1).join('*'),
    visiblePart: accountNumber.slice(-4),
  };
};

export const uuid = (): string => {
  let d = new Date().getTime();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);

    // eslint-disable-next-line no-bitwise
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const validateRedirectURI = (uriString: string) => {
  try {
    const uri = new URL(uriString);

    if (uri.protocol === 'http:' && uri.hostname !== 'localhost') {
      return false;
    }

    // eslint-disable-next-line no-script-url
    if (uri.protocol === 'javascript:' || uri.protocol === 'data:') {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};
